import {
          Alert,
          Box,
          Stack,
          TextField,
          Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthButton from "../components/auth/AuthButton";
import AuthFormContainer from "../components/auth/AuthFormContainer";
import { useUser } from "../hooks/useAuth";
import { apiClient } from "../utils/api";
import { isSpamMessage } from "../utils/contactFormSpam";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

interface ContactFormData {
          full_name?: string;
          email?: string;
          phone_number?: string;
          message: string;
}

const ContactUsPage: React.FC = () => {
          const { t } = useTranslation();
          const { data: user } = useUser();
          const isLoggedIn = !!user;

          useEffect(() => {
                    trackEvent(MixpanelEvents.CONTACT_PAGE_VIEWED, { logged_in: isLoggedIn });
          }, [isLoggedIn]);

          const [formData, setFormData] = useState<ContactFormData>({
                    full_name: "",
                    email: "",
                    phone_number: "",
                    message: "",
          });
          const [errors, setErrors] = useState<Record<string, string>>({});
          const [success, setSuccess] = useState(false);

          // Pre-fill form if user is logged in
          useEffect(() => {
                    if (isLoggedIn && user) {
                              const fullName = user.first_name && user.last_name
                                        ? `${user.first_name} ${user.last_name}`
                                        : user.username || "";

                              setFormData((prev) => ({
                                        ...prev,
                                        full_name: fullName,
                                        email: user.email || "",
                              }));
                    }
          }, [isLoggedIn, user]);

          const submitMutation = useMutation({
                    mutationFn: (data: ContactFormData) => apiClient.submitContactForm(data),
                    onSuccess: () => {
                              trackEvent(MixpanelEvents.CONTACT_FORM_SUBMITTED, { logged_in: isLoggedIn });
                              setSuccess(true);
                              setFormData({
                                        full_name: isLoggedIn ? formData.full_name : "",
                                        email: isLoggedIn ? formData.email : "",
                                        phone_number: "",
                                        message: "",
                              });
                              setErrors({});
                              // Hide success message after 5 seconds
                              setTimeout(() => setSuccess(false), 5000);
                    },
                    onError: (error: any) => {
                              trackEvent(MixpanelEvents.CONTACT_FORM_FAILED, {
                                        status: error.response?.status,
                              });
                              if (error.response?.data) {
                                        const apiErrors = error.response.data;
                                        const newErrors: Record<string, string> = {};

                                        // Handle field-specific errors
                                        if (apiErrors.full_name) {
                                                  newErrors.full_name = Array.isArray(apiErrors.full_name)
                                                            ? apiErrors.full_name[0]
                                                            : apiErrors.full_name;
                                        }
                                        if (apiErrors.email) {
                                                  newErrors.email = Array.isArray(apiErrors.email)
                                                            ? apiErrors.email[0]
                                                            : apiErrors.email;
                                        }
                                        if (apiErrors.message) {
                                                  newErrors.message = Array.isArray(apiErrors.message)
                                                            ? apiErrors.message[0]
                                                            : apiErrors.message;
                                        }
                                        if (apiErrors.phone_number) {
                                                  newErrors.phone_number = Array.isArray(apiErrors.phone_number)
                                                            ? apiErrors.phone_number[0]
                                                            : apiErrors.phone_number;
                                        }

                                        setErrors(newErrors);
                              } else {
                                        setErrors({ message: t("contact.error") });
                              }
                    },
          });

          const validate = (): boolean => {
                    const newErrors: Record<string, string> = {};

                    // For anonymous users, full_name and email are required
                    if (!isLoggedIn) {
                              if (!formData.full_name?.trim()) {
                                        newErrors.full_name = t("contact.fullNameRequired");
                              }

                              if (!formData.email?.trim()) {
                                        newErrors.email = t("contact.emailRequired");
                              } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                                        newErrors.email = t("auth.emailInvalid");
                              }
                    } else {
                              // For logged-in users, validate email if provided
                              if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                                        newErrors.email = t("auth.emailInvalid");
                              }
                    }

                    // Message is always required
                    if (!formData.message?.trim()) {
                              newErrors.message = t("contact.messageRequired");
                    } else if (formData.message.trim().length < 10) {
                              newErrors.message = t("contact.messageMinLength");
                    } else if (isSpamMessage(formData.message)) {
                              newErrors.message = t("contact.spamDetected");
                    }

                    // Validate phone number format if provided
                    if (formData.phone_number && formData.phone_number.trim()) {
                              const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
                              if (!phoneRegex.test(formData.phone_number.trim())) {
                                        newErrors.phone_number = t("contact.phoneInvalid");
                              }
                              if (formData.phone_number.trim().length > 20) {
                                        newErrors.phone_number = t("contact.phoneMaxLength");
                              }
                    }

                    setErrors(newErrors);
                    return Object.keys(newErrors).length === 0;
          };

          const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!validate()) {
                              return false;
                    }

                    // Prepare data for API
                    const submitData: ContactFormData = {
                              message: formData.message.trim(),
                    };

                    // Only include optional fields if they have values
                    if (formData.full_name?.trim()) {
                              submitData.full_name = formData.full_name.trim();
                    }
                    if (formData.email?.trim()) {
                              submitData.email = formData.email.trim();
                    }
                    if (formData.phone_number?.trim()) {
                              submitData.phone_number = formData.phone_number.trim();
                    }

                    submitMutation.mutate(submitData);
                    return false;
          };

          return (
                    <AuthFormContainer>
                              <Box
                                        component="form"
                                        onSubmit={handleSubmit}
                                        noValidate
                                        action="#"
                              >
                                        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                                                  <Stack spacing={{ xs: 0.25, md: 0.5 }}>
                                                            <Typography
                                                                      variant="h4"
                                                                      fontWeight={700}
                                                                      sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
                                                            >
                                                                      {t("contact.title")}
                                                            </Typography>
                                                            <Typography
                                                                      variant="body2"
                                                                      sx={{
                                                                                color: "text.secondary",
                                                                                fontSize: { xs: "0.75rem", md: "0.875rem" },
                                                                      }}
                                                            >
                                                                      {t("contact.description")}
                                                            </Typography>
                                                  </Stack>

                                                  {success && (
                                                            <Alert severity="success" sx={{ py: { xs: 0.5, md: 1 } }}>
                                                                      {t("contact.success")}
                                                            </Alert>
                                                  )}

                                                  {submitMutation.error && !success && (
                                                            <Alert severity="error" sx={{ py: { xs: 0.5, md: 1 } }}>
                                                                      {(submitMutation.error as any)?.response?.data?.detail ||
                                                                                t("contact.error")}
                                                            </Alert>
                                                  )}

                                                  <Stack spacing={{ xs: 1.5, md: 2 }}>
                                                            <TextField
                                                                      fullWidth
                                                                      label={t("contact.fullName")}
                                                                      value={formData.full_name || ""}
                                                                      onChange={(e) =>
                                                                                setFormData({ ...formData, full_name: e.target.value })
                                                                      }
                                                                      error={!!errors.full_name}
                                                                      helperText={errors.full_name}
                                                                      disabled={isLoggedIn}
                                                                      required={!isLoggedIn}
                                                                      sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                          backgroundColor: "background.default",
                                                                                },
                                                                      }}
                                                            />

                                                            <TextField
                                                                      fullWidth
                                                                      type="email"
                                                                      label={t("contact.email")}
                                                                      value={formData.email || ""}
                                                                      onChange={(e) =>
                                                                                setFormData({ ...formData, email: e.target.value })
                                                                      }
                                                                      error={!!errors.email}
                                                                      helperText={errors.email}
                                                                      disabled={isLoggedIn}
                                                                      required={!isLoggedIn}
                                                                      autoComplete="email"
                                                                      sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                          backgroundColor: "background.default",
                                                                                },
                                                                      }}
                                                            />

                                                            <TextField
                                                                      fullWidth
                                                                      type="tel"
                                                                      label={t("contact.phoneNumber")}
                                                                      value={formData.phone_number || ""}
                                                                      onChange={(e) =>
                                                                                setFormData({ ...formData, phone_number: e.target.value })
                                                                      }
                                                                      error={!!errors.phone_number}
                                                                      helperText={errors.phone_number}
                                                                      placeholder={t("contact.phonePlaceholder")}
                                                                      sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                          backgroundColor: "background.default",
                                                                                },
                                                                      }}
                                                            />

                                                            <TextField
                                                                      fullWidth
                                                                      multiline
                                                                      rows={6}
                                                                      label={t("contact.message")}
                                                                      value={formData.message}
                                                                      onChange={(e) =>
                                                                                setFormData({ ...formData, message: e.target.value })
                                                                      }
                                                                      error={!!errors.message}
                                                                      helperText={errors.message}
                                                                      required
                                                                      sx={{
                                                                                "& .MuiInputBase-root": {
                                                                                          backgroundColor: "background.default",
                                                                                },
                                                                      }}
                                                            />
                                                  </Stack>

                                                  <AuthButton
                                                            type="submit"
                                                            variant="primary"
                                                            loading={submitMutation.isPending}
                                                  >
                                                            {t("contact.submit")}
                                                  </AuthButton>
                                        </Stack>
                              </Box>
                    </AuthFormContainer>
          );
};

export default ContactUsPage;
